import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable } from "../../components/DataTable/TableHook";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { RootState } from "../../state/store";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useOptions } from "../../components/Hooks/useOptions";
import { InsertGeoRehandleRow } from "./InsertGeologyRehandleRow";
import { DeleteButton } from "../../components/Buttons/DeleteButton";
import { GeoRehandleDiggerDTO } from "../../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { TripleShift } from "../../types/Shift";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import DiggerCell from "./TableCellsComponents/DiggerCell";
import PlantModelCell from "./TableCellsComponents/PlantModelCell";
import MovementFromCell from "./TableCellsComponents/MovementFromCell";
import MovementToCell from "./TableCellsComponents/MovementToCell";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { GetGeoRehandlesPagedResultDTO } from "../../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import GeologyRehandleTableFooter from "./GeologyRehandleTableFooter";
import GeologyRehandleTableNoResultsContent from "./GeologyRehandleNoResultsContent";
import { TablePage } from "../../components/Layout/TablePage";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { geoRehandleMutations } from "../../offline/geoRehandleMutations";

type GeologyRehandleTableMetaData = {
  totalLoads: number;
  totalTonnes: number;
}

const GeologyRehandle: React.FC = () => {
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const canToggleLock = useSelector((s: RootState) => s.shift.canToggleLock);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const { selectedDate, selectedShift } = useShiftTimes();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)

  const setLocalSelectedShift = (newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  };

  const [insertRowState, setInsertRowState] = useState<GeoRehandleDTO>(new GeoRehandleDTO({
    geoRehandleId: 0,
    operationId: 0,
    bucketTypeId: 0,
    movementFromId: 0,
    movementToId: 0
  }));

  const {
    options: diggerOptions,
    isLoading: isDiggerOptionsLoading
  } = useOptions<GeoRehandleDiggerDTO, GeoRehandleDiggerDTO>(
    ['geology', 'rehandleDiggers', selectedDate.toISODate(), localSelectedShift],
    () => geologyAjax.getRehandleDiggers(selectedDate, localSelectedShift),
    x => x,
  );

  const {
    options: bucketTypeOptions,
    isLoading: bucketTypeOptionsLoading
  } = useOptions<GeoRehandleTruckingTruckDTO, GeoRehandleTruckingTruckDTO>(
    ['geology', 'rehandleTrucks', selectedDate.toISODate()],
    () => geologyAjax.getRehandleTruckingTrucks(selectedDate),
    x => x,
  );

  const {
    options: rehandleMovementFromLocationOptions,
    isLoading: isRehandleMovementFromLocationOptionsLoading
  } = useOptions<LocationDTO, { value: number, label: string, isDumpOrCrusher: boolean }>(
    ['geology', 'rehandleFromLocations', selectedDate.toISODate()],
    () => geologyAjax.getRehandleMovementFromLocations(selectedDate),
    x => ({
      value: x.id ?? 0,
      label: x.name,
      isDumpOrCrusher: x.locationTypeId === 3 || x.locationTypeId === 6
    }),
  );

  const {
    options: rehandleMovementToLocationOptions,
    isLoading: isRehandleMovementToLocationOptionsLoading
  } = useOptions<LocationDTO, { value: number, label: string, isDumpOrCrusher: boolean }>(
    ['geology', 'rehandleToLocations', selectedDate.toISODate()],
    () => geologyAjax.getRehandleMovementToLocations(selectedDate),
    x => ({
      value: x.id ?? 0,
      label: x.name,
      isDumpOrCrusher: x.locationTypeId === 3 || x.locationTypeId === 6
    }),
  );

  const handleUpdateRow = useCallback(async (row: GeoRehandleDTO) => {
    let isValid = true;

    if (!row.operationId) {
      isValid = false;
      enqueueSnackbar('Please select a Digger', { variant: 'error' });
    }
    if (!row.bucketTypeId) {
      isValid = false;
      enqueueSnackbar('Please select a bucket type', { variant: 'error' });
    }
    if (!row.movementFromId) {
      isValid = false;
      enqueueSnackbar('Please select a location to move from', { variant: 'error' });
    }
    if (!row.loads) {
      isValid = false;
      enqueueSnackbar('Please enter the number of loads to move', { variant: 'error' });
    }
    if (!row.movementToId) {
      isValid = false;
      enqueueSnackbar('Please select a location to move to', { variant: 'error' });
    }
    if (row.movementFromId === row.movementToId) {
      isValid = false;
      enqueueSnackbar('Please select a different location to move to', { variant: 'error' });
    }

    if (!isValid || row.geoRehandleId === 0) return;

    const response = await geoRehandleMutations.updateGeoRehandle(row)

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    enqueueSnackbar('Rehandle Entry updated successfully', { variant: 'success' });
    const { rehandle, totalLoads, totalTonnes } = response.data!;
    updateRowSource((r) => r.geoRehandleId === rehandle!.geoRehandleId, rehandle!, { totalLoads, totalTonnes });

    return true;
  }, [enqueueSnackbar]);

  const handleAddRow = useCallback(async () => {
    let isValid = true;

    if (!insertRowState.operationId) {
      enqueueSnackbar('Please select a Digger', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.bucketTypeId) {
      enqueueSnackbar('Please select a Plant Model', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.movementFromId) {
      enqueueSnackbar('Please select a location to move from', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.loads) {
      enqueueSnackbar('Please enter the number of loads to move', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.movementToId) {
      enqueueSnackbar('Please select a location to move to', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await geoRehandleMutations.addGeoRehandle(insertRowState, selectedDate, localSelectedShift);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 * (index + 1) });
      });
      return false;
    }

    enqueueSnackbar('Rehandle Entry added successfully', { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    appendRowSource(rehandle!, { totalLoads, totalTonnes });
    setInsertRowState(new GeoRehandleDTO({
      geoRehandleId: 0,
      operationId: 0,
      bucketTypeId: 0,
      movementFromId: 0,
      movementToId: 0
    }));
    return true;
  }, [insertRowState, localSelectedShift, selectedDate, enqueueSnackbar]);

  const deleteRow = useCallback(async (rowId: number) => {
    const response = await geoRehandleMutations.deleteGeoRehandle(rowId);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    const { totalLoads, totalTonnes } = response.data!;
    removeRowSource((r) => r.geoRehandleId === rowId, { totalLoads, totalTonnes });
    enqueueSnackbar('Ore Rehandle entry deleted successfully', { variant: 'success', autoHideDuration: 3000 })

    return true;
  }, [enqueueSnackbar]);

  const getRows = useCallback(() => geologyAjax.getGeoRehandles(selectedDate, localSelectedShift), [selectedDate, localSelectedShift]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        {
          name: "Digger",
          editable: false,
          type: "custom",
          value: entry.operationId,
          tailwindClasses: "basis-[20%] py-1 pl-1",
          render: () => (
            <DiggerCell
              entry={entry}
              diggerOptions={diggerOptions}
              isDiggerOptionsLoading={isDiggerOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isShiftLocked={isShiftLocked}
            />
          )
        },
        {
          name: "Plant Model",
          editable: false,
          type: "custom",
          value: entry.bucketTypeId,
          tailwindClasses: "basis-[20%] py-1",
          render: () => (
            <PlantModelCell
              entry={entry}
              bucketTypeOptions={bucketTypeOptions}
              bucketTypeOptionsLoading={bucketTypeOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isShiftLocked={isShiftLocked}
            />
          )
        },
        {
          name: "Movement From",
          editable: false,
          type: "custom",
          value: entry.movementFromId,
          tailwindClasses: "basis-[20%]",
          render:
            () => (
              <MovementFromCell
                entry={entry}
                locationOptions={rehandleMovementFromLocationOptions}
                locationOptionsLoading={isRehandleMovementFromLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isShiftLocked={isShiftLocked}
              />
            )
        },
        {
          name: "Loads",
          editable: true,
          type: "integer",
          value: entry.loads,
          tailwindClasses: "basis-[10%] py-1",
          disabled: isShiftLocked,
          onChangeDebounced: async (_, newValue) => await handleUpdateRow({ ...entry, loads: newValue }),
          onChangeDebouncedMs: 1200,
        },
        {
          name: "Movement To",
          editable: false,
          type: "custom",
          value: entry.movementToId,
          tailwindClasses: "basis-[20%]",
          render:
            () => (
              <MovementToCell
                entry={entry}
                locationOptions={rehandleMovementToLocationOptions}
                locationOptionsLoading={isRehandleMovementToLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isShiftLocked={isShiftLocked}
              />
            )
        },
        {
          name: 'Actions',
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'basis-[10%] pr-1',
          render: (_, tableRow) =>
            <div className="flex justify-center flex-wrap gap-1">
              <DeleteButton
                handleDelete={() => deleteRow(tableRow.id)}
                disabled={isShiftLocked}
              />
            </div>
        }
      ]
    }
  }, [
    bucketTypeOptions,
    bucketTypeOptionsLoading,
    rehandleMovementToLocationOptions,
    rehandleMovementFromLocationOptions,
    isRehandleMovementToLocationOptionsLoading,
    isRehandleMovementFromLocationOptionsLoading,
    diggerOptions,
    isDiggerOptionsLoading,
    handleUpdateRow,
    deleteRow,
    isShiftLocked
  ]);

  const getGeologyRehandleTableMetaData = useCallback((response: GetGeoRehandlesPagedResultDTO) => ({
    totalLoads: response.totalLoads,
    totalTonnes: response.totalTonnes,
  }), []);

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalPages,
    totalCount,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    removeRowSource,
    updateRowSource,
    invalidate,
    tableMetaData
  } = useDataTable<GeoRehandleDTO, number, GeologyRehandleTableMetaData, GetGeoRehandlesPagedResultDTO>({
    queryKey: ['geology', 'rehandles', selectedDate.toISODate(), localSelectedShift],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: 15,
    selectMeta: getGeologyRehandleTableMetaData
  });

  useEffect(() => {
    const newView = "GeologyRehandle";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const handleGeoMovementsChanged = useCallback(() => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['geology', 'rehandleFromLocations'] });
    queryClient.invalidateQueries({ queryKey: ['geology', 'rehandleToLocations'] });
  }, [invalidate, queryClient]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleGeoMovementsChanged);
    return () => {
      connection.off("DataUpdatedAsync", handleGeoMovementsChanged);
    };
  }, [connection, handleGeoMovementsChanged]);

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText={"Rehandle"} />
      </Breadcrumbs>
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={error}
        pageNumber={pageNumber}
        totalCount={totalCount}
        pageSize={pageSize}
        totalPages={totalPages}
        setPage={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1080}
        fillHeight
        noResultsContent={<GeologyRehandleTableNoResultsContent
          isShiftLocked={isShiftLocked}
          canToggleLock={canToggleLock}
        />}
        tableFooterContent={<GeologyRehandleTableFooter
          isLoading={isLoading}
          totalLoads={tableMetaData?.totalLoads}
        />}
        insertRowRenderer={() => <InsertGeoRehandleRow
          state={insertRowState}
          setState={setInsertRowState}
          diggerOptions={diggerOptions}
          isDiggerOptionsLoading={isDiggerOptionsLoading}
          bucketTypeOptions={bucketTypeOptions}
          bucketTypeOptionsLoading={bucketTypeOptionsLoading}
          rehandleMovementFromLocationOptions={rehandleMovementFromLocationOptions}
          isRehandleMovementFromLocationOptionsLoading={isRehandleMovementFromLocationOptionsLoading}
          rehandleMovementToLocationOptions={rehandleMovementToLocationOptions}
          isRehandleMovementToLocationOptionsLoading={isRehandleMovementToLocationOptionsLoading}
          onAdd={handleAddRow}
          isShiftLocked={isShiftLocked}
        />}
        overrideHeaderCells={[
          { name: "Digger", tailwindClasses: "basis-[20%] py-1 pl-1" },
          { name: "Plant Model", tailwindClasses: "basis-[20%] py-1" },
          { name: "Movement From", tailwindClasses: "basis-[20%]" },
          { name: "Loads", tailwindClasses: "basis-[10%] py-1" },
          { name: "Movement To", tailwindClasses: "basis-[20%]" },
          { name: "Actions", tailwindClasses: "basis-[10%] pr-1" }
        ]}
      />
    </TablePage>
  )
}

export default GeologyRehandle